import { useSearchParams } from 'react-router-dom'
import { useCallback, useMemo, useRef } from 'react'

/**
 * Generic hook for managing state in URL search params
 * Updates URL without creating new history entries (replace: true)
 */
export function useUrlState<T extends Record<string, any>>(
  defaultValues: T,
  serializers: {
    [K in keyof T]?: {
      serialize: (value: T[K]) => string | undefined
      deserialize: (value: string | null) => T[K]
    }
  } = {}
) {
  const [searchParams, setSearchParams] = useSearchParams()

  // Callers build `defaultValues` and `serializers` as object literals inside
  // their own render, so they are new on every render and can never be
  // dependencies. Read them through a ref and key the work off `searchParams`,
  // which is the only input that actually changes.
  const latest = useRef({ defaultValues, serializers })
  latest.current = { defaultValues, serializers }

  // Deserialize current state from URL. Memoized because the array serializers
  // return a new array per call: recomputing per render handed every caller a
  // new `state` object with new arrays inside it, so any effect depending on
  // one of those arrays re-ran on every render and looped through its setState.
  const state = useMemo((): T => {
    const { defaultValues: defaults, serializers: parsers } = latest.current
    const next = { ...defaults }

    for (const key in defaults) {
      const paramValue = searchParams.get(key)
      const serializer = parsers[key]

      if (serializer && serializer.deserialize) {
        next[key] = serializer.deserialize(paramValue)
      } else if (paramValue !== null) {
        // Default deserialization
        next[key] = paramValue as T[typeof key]
      }
    }

    return next
  }, [searchParams])

  // Update URL with new state
  const updateUrlState = useCallback(
    (updates: Partial<T>) => {
      const newParams = new URLSearchParams(searchParams)

      for (const key in updates) {
        const value = updates[key]
        const serializer = latest.current.serializers[key]

        let serializedValue: string | undefined

        if (serializer && serializer.serialize) {
          serializedValue = serializer.serialize(value)
        } else if (value !== undefined && value !== null && value !== '') {
          // Default serialization
          serializedValue = String(value)
        }

        if (serializedValue !== undefined && serializedValue !== '') {
          newParams.set(key, serializedValue)
        } else {
          newParams.delete(key)
        }
      }

      // Use replace: true to avoid creating history entries for filter changes
      setSearchParams(newParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  return {
    state,
    updateState: updateUrlState,
  }
}

